import { defineStore } from 'pinia';
import { ProductService } from '@/services/product';

interface FormsState {
  /** All bot forms for the current bot; false while not loaded */
  forms: any[] | false;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/forms.ts` (namespace `forms`).
 * In-memory only — legacy module was never listed in `src/store/index.ts`'s persistence
 * reducers, so no `vuexPersist` option here.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH state prop `forms` and an identity-passthrough getter `forms`. Per the
 *   state-prop/getter collision rule, the getter is renamed `formsGetter`; consumers should
 *   prefer reading `useFormsStore().forms` directly off state anyway.
 * - Legacy mutations `setForms` / `addForm` had no same-named actions — they become same-named
 *   actions here.
 */
export const useFormsStore = defineStore('forms', {
  state: (): FormsState => ({
    forms: false,
  }),
  getters: {
    /**
     * Renamed from legacy getter `forms` (collides with state prop `forms` of the same name —
     * the state prop keeps the name). Identity passthrough — prefer reading `.forms` directly.
     */
    formsGetter(state): any[] | false {
      return state.forms;
    },
  },
  actions: {
    /* async add({commit}, {botId, samples, displayName, description, suggested, forward}) {
          let result = await ProductService.addIntent(botId, samples, displayName, description, suggested, forward);

          if (!result.error && result.intent) {
              commit('addIntent', result.intent);
          }

          return result;
      }, */
    async add({ botId, displayName, template }: { botId: string; displayName: string; template: any }) {
      let result: any = await ProductService.addForm(botId, displayName, template);
      if (!result.error && result.form) {
        this.addForm(result.form);
      }

      return result;
    },
    async get(uniqueBotId: string) {
      this.setForms(false);

      const formsResponse: any = await ProductService.getAllForms(uniqueBotId);

      if (formsResponse && formsResponse.status === 'ok' && formsResponse.forms) {
        this.setForms(formsResponse.forms);
      }

      return true;
    },
    async getByBotId(uniqueBotId: string) {
      const formResponse: any = await ProductService.getAllForms(uniqueBotId);

      if (formResponse && formResponse.status === 'ok' && formResponse.forms) {
        return formResponse.forms;
      }

      return false;
    },
    /**
     * Converted from legacy mutation `setForms` (no matching legacy action existed).
     */
    setForms(forms: any[] | false) {
      if (Array.isArray(forms)) {
        forms.sort((a, b) => {
          return a.displayName
            .toLowerCase()
            .localeCompare(b.displayName.toLowerCase());
        });
      }

      this.forms = forms;
    },
    /**
     * Converted from legacy mutation `addForm` (no matching legacy action existed).
     */
    addForm(form: any) {
      (this.forms as any[]).unshift(form);
    },
  },
});
